import { useCallback, useState, type MouseEvent } from 'react';
import ThemeTray from '../../../components/theme-tray/theme-tray';
import ThemeInfoPill from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/theme-info-pill/theme-info-pill';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import useGetWorkingProjectThemeMode from '@/utils/use-get-working-project-theme-mode';
import useGetEmailCampaignsThemePack from '@/utils/use-get-email-campaigns-theme-pack';
import { type ThemePack } from '@/types/theme-packs.types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setCampaignEditorField } from '@/store/slices/campaign-editor';
import { selectThemeColorPackId } from '@/store/slices/campaign-editor/selectors';

const CAMPAIGNS_THEME_SAVE_ID = 'campaigns-theme-pack-save';
const NOOP = () => {};

const CampaignsThemeTray = () => {
  const dispatch = useAppDispatch();
  const themeColorPackId = useAppSelector(selectThemeColorPackId);
  const [open, setOpen] = useState(false);
  const [liveEditedPack, setLiveEditedPack] = useState<ThemePack | null>(null);
  const resolvedThemePack = useGetEmailCampaignsThemePack(themeColorPackId);
  const projectThemeMode = useGetWorkingProjectThemeMode();
  const displayThemePack = liveEditedPack ?? resolvedThemePack;
  const hasAppliedTheme = liveEditedPack !== null || themeColorPackId !== undefined;

  const handleThemePackChange = useCallback(
    (selectedThemePack: ThemePack) => {
      setLiveEditedPack(null);
      dispatch(setCampaignEditorField({ key: 'themeColorPackId', value: selectedThemePack.color_pack }));
      // theme mode set once when selecting the theme and remains static until changed
      // and no longer follows project theme mode if the project theme changes
      dispatch(setCampaignEditorField({ key: 'themeMode', value: projectThemeMode }));
    },
    [dispatch, projectThemeMode]
  );

  const handleThemePackUpdate = useCallback((updatedThemePack: ThemePack) => {
    setLiveEditedPack(updatedThemePack);
  }, []);

  const handleRemoveTheme = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setLiveEditedPack(null);
      dispatch(setCampaignEditorField({ key: 'themeColorPackId', value: undefined }));
      dispatch(setCampaignEditorField({ key: 'themeMode', value: undefined }));
    },
    [dispatch]
  );

  const handleClose = useCallback(() => setOpen(false), []);
  const handleToggle = useCallback(() => setOpen((prev) => !prev), []);

  if (!displayThemePack) return null;

  const carouselActivePack =
    liveEditedPack ?? (themeColorPackId !== undefined ? displayThemePack : { ...displayThemePack, color_pack: -1 });

  return (
    <>
      {hasAppliedTheme ? (
        <Container className="flex ai-center gap-4">
          <ThemeInfoPill themePack={displayThemePack} themeMode={projectThemeMode} open={open} onClick={handleToggle} />
          <Button
            clear
            className="ai-center jc-center flex-shrink-0"
            width="24px"
            height="24px"
            padding="0"
            design="LocalBttnSec"
            hoverChildrenColor="var(--global-ico-text-1)"
            onClick={handleRemoveTheme}
            aria-label="Remove theme"
            title="Remove theme"
          >
            <Icon name={ICONS.CLOSE} size={12} />
          </Button>
        </Container>
      ) : (
        <Button
          data-component="ThemeInfoPill"
          backgroundColor={open ? 'var(--global-cont-2)' : 'var(--global-cont-1)'}
          hoverBackgroundColor="var(--global-cont-2)"
          height="32px"
          borderRadius="100px"
          className="P2"
          onClick={handleToggle}
          name="theme-info-toggle"
        >
          Choose a theme
        </Button>
      )}
      <ThemeTray
        activeThemePack={carouselActivePack}
        activeThemeMode={projectThemeMode}
        onThemeModeChange={NOOP}
        showModeToggle={false}
        onThemePackChange={handleThemePackChange}
        onThemePackUpdate={handleThemePackUpdate}
        onClose={handleClose}
        open={open}
        saveId={CAMPAIGNS_THEME_SAVE_ID}
        portalZIndex={101}
        editable={false}
        editableFont
        editableCorners
        editableColors
        showLogoTab={false}
      />
    </>
  );
};

export default CampaignsThemeTray;
